
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import AddDialog from "../../components/Modal/AddModal";
import TableLoadingRow from "../../components/TableLoadingRow";
import { addIncomeService, getIncomeService, getIncomeTypeService } from "../../services/incomeService";
import { addExpenseService, getExpensesService, getExpensesTypeService } from "../../services/expensesService";
import { showErrorToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";
import { FEATURE_KEYS, hasFeature } from "../../utils/featureAccess";

type EntryKind = "income" | "expense";

type EntryRow = {
    id: string;
    kind: EntryKind;
    date: string;
    amount: number;
    typeName: string;
    note: string;
    source: string;
};

type AccountingSummaryRow = {
    key: string;
    category: "income" | "expense" | "salary";
    typeName: string;
    details: string;
    amount: number;
};

type TypeOption = {
    id: string;
    name: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
};

const toTypeOptions = (items: unknown[]): TypeOption[] =>
    items
        .map((item) => {
            const parsed = asRecord(item);
            const id = asString(parsed._id) || asString(parsed.id);
            const name = asString(parsed.name);
            if (!id || !name) return null;
            return { id, name };
        })
        .filter((item): item is TypeOption => item !== null);

const dateText = (value: string): string => {
    if (!value) return "-";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toISOString().split("T")[0];
};

const normalizeDateOnly = (value: unknown): string => {
    const raw = asString(value);
    if (!raw) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    return dateText(raw);
};

const createTypeMap = (items: TypeOption[]): Map<string, string> => {
    const map = new Map<string, string>();
    items.forEach((item) => map.set(item.id, item.name));
    return map;
};

const toDateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    return {
        startDate: toDateInputValue(start),
        endDate: toDateInputValue(end),
    };
};

const resolveTypeName = (
    entry: Record<string, unknown>,
    kind: EntryKind,
    typeMap: Map<string, string>
): string => {
    const directName = asString(entry.typeNameSnapshot) || asString(entry.typeName) || asString(entry.name);
    if (directName) return directName;

    const nestedType =
        asRecord(kind === "income" ? entry.incomeType : entry.expenseType);
    const genericType = asRecord(entry.typeId);
    const explicitType = asRecord(entry.type);
    const nestedName = asString(nestedType.name);
    if (nestedName) return nestedName;
    const genericName = asString(genericType.name) || asString(explicitType.name);
    if (genericName) return genericName;

    const typeId =
        asString(entry.incomeTypeId) ||
        asString(entry.expenseTypeId) ||
        asString(nestedType._id) ||
        asString(nestedType.id) ||
        asString(genericType._id) ||
        asString(genericType.id) ||
        asString(explicitType._id) ||
        asString(explicitType.id) ||
        asString(entry.type);

    return typeMap.get(typeId) ?? "-";
};

const normalizeEntries = (
    rows: unknown[],
    kind: EntryKind,
    typeMap: Map<string, string>
): EntryRow[] => {
    return rows.map((item) => {
        const parsed = asRecord(item);

        const id = asString(parsed._id) || asString(parsed.id) || crypto.randomUUID();
        const date = normalizeDateOnly(parsed.date || parsed.createdAt || parsed.createdDate || parsed.transactionDate);
        const amount = asNumber(parsed.amount) || asNumber(parsed.total) || asNumber(parsed.value);
        const note = asString(parsed.note) || asString(parsed.notes) || asString(parsed.description) || "";
        const source = asString(parsed.source) || "manual";

        return {
            id,
            kind,
            date,
            amount,
            typeName: resolveTypeName(parsed, kind, typeMap),
            note,
            source,
        };
    });
};

const AccountingPage = () => {
    const { user } = useAuth();
    const canEditAccounting = hasPermission(user, "accounting.write");
    const canUseAccounting = hasFeature(FEATURE_KEYS.ACCOUNTING);
    const canUseIncome = canUseAccounting;
    const canUseExpenses = canUseAccounting;
    const canUseIncomeTypes = canUseAccounting;
    const canUseExpenseTypes = canUseAccounting;
    const [rows, setRows] = useState<EntryRow[]>([]);
    const [incomeTypes, setIncomeTypes] = useState<TypeOption[]>([]);
    const [expenseTypes, setExpenseTypes] = useState<TypeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [showIncomeModal, setShowIncomeModal] = useState(false);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [incomeForm, setIncomeForm] = useState({
        typeId: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        note: "",
    });
    const [expenseForm, setExpenseForm] = useState({
        typeId: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        note: "",
    });

    const [kindFilter, setKindFilter] = useState<"all" | EntryKind>("all");
    const [typeFilter, setTypeFilter] = useState("all");
    const [dateRange, setDateRange] = useState(getCurrentMonthRange);

    const fetchAccounting = useCallback(async () => {
        if (!user?.id) return;

        try {
            setLoading(true);
            setError("");

            const [incomeTypesRaw, expenseTypesRaw, incomeRaw, expenseRaw] = await Promise.all([
                canUseIncomeTypes ? getIncomeTypeService() : Promise.resolve([]),
                canUseExpenseTypes ? getExpensesTypeService() : Promise.resolve([]),
                canUseIncome ? getIncomeService(dateRange) : Promise.resolve([]),
                canUseExpenses ? getExpensesService(dateRange) : Promise.resolve([]),
            ]);

            const normalizedIncomeTypes = toTypeOptions(incomeTypesRaw);
            const normalizedExpenseTypes = toTypeOptions(expenseTypesRaw);

            setIncomeTypes(normalizedIncomeTypes);
            setExpenseTypes(normalizedExpenseTypes);

            const incomeTypeMap = createTypeMap(normalizedIncomeTypes);
            const expenseTypeMap = createTypeMap(normalizedExpenseTypes);

            const incomeRows = normalizeEntries(incomeRaw, "income", incomeTypeMap);
            const expenseRows = normalizeEntries(expenseRaw, "expense", expenseTypeMap);

            const merged = [...incomeRows, ...expenseRows].sort(
                (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            );

            setRows(merged);
        } catch (err) {
            console.error("Failed to fetch accounting entries", err);
            setError("Failed to load accounting data");
        } finally {
            setLoading(false);
        }
    }, [user?.id, dateRange, canUseIncome, canUseExpenses, canUseIncomeTypes, canUseExpenseTypes]);

    useEffect(() => {
        fetchAccounting();
    }, [fetchAccounting]);

    const resetIncomeForm = () => {
        setIncomeForm({
            typeId: "",
            amount: "",
            date: new Date().toISOString().split("T")[0],
            note: "",
        });
    };

    const resetExpenseForm = () => {
        setExpenseForm({
            typeId: "",
            amount: "",
            date: new Date().toISOString().split("T")[0],
            note: "",
        });
    };

    const closeIncomeModal = () => {
        setShowIncomeModal(false);
        resetIncomeForm();
    };

    const closeExpenseModal = () => {
        setShowExpenseModal(false);
        resetExpenseForm();
    };

    const handleAddIncome = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!user?.id) return;
        if (!canUseIncome) {
            showErrorToast("Income is not enabled for this gym");
            return;
        }
        if (!incomeForm.typeId || !incomeForm.amount) {
            showErrorToast("Please select income type and amount");
            return;
        }

        try {
            setSaving(true);
            await addIncomeService({
                incomeTypeId: incomeForm.typeId,
                amount: Number(incomeForm.amount),
                date: incomeForm.date,
                note: incomeForm.note,
            });
            closeIncomeModal();
            await fetchAccounting();
        } catch (err) {
            console.error("Failed to add income", err);
            showErrorToast("Failed to add income");
        } finally {
            setSaving(false);
        }
    };

    const handleAddExpense = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!user?.id) return;
        if (!canUseExpenses) {
            showErrorToast("Expenses are not enabled for this gym");
            return;
        }
        if (!expenseForm.typeId || !expenseForm.amount) {
            showErrorToast("Please select expense type and amount");
            return;
        }

        try {
            setSaving(true);
            await addExpenseService({
                expenseTypeId: expenseForm.typeId,
                amount: Number(expenseForm.amount),
                date: expenseForm.date,
                note: expenseForm.note,
            });
            closeExpenseModal();
            await fetchAccounting();
        } catch (err) {
            console.error("Failed to add expense", err);
            showErrorToast("Failed to add expense");
        } finally {
            setSaving(false);
        }
    };

    const availableTypes = useMemo(() => {
        const unique = new Set<string>();
        rows.forEach((row) => {
            if (row.kind === "income" && !canUseIncome) return;
            if (row.kind === "expense" && !canUseExpenses) return;
            if (kindFilter !== "all" && row.kind !== kindFilter) return;
            if (row.typeName && row.typeName !== "-") unique.add(row.typeName);
        });
        return Array.from(unique).sort((a, b) => a.localeCompare(b));
    }, [rows, kindFilter, canUseIncome, canUseExpenses]);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            if (row.kind === "income" && !canUseIncome) return false;
            if (row.kind === "expense" && !canUseExpenses) return false;
            if (kindFilter !== "all" && row.kind !== kindFilter) return false;
            if (typeFilter !== "all" && row.typeName !== typeFilter) return false;

            const rowDate = normalizeDateOnly(row.date);
            if (!rowDate) return false;
            if (dateRange.startDate && rowDate < dateRange.startDate) return false;
            if (dateRange.endDate && rowDate > dateRange.endDate) return false;

            return true;
        });
    }, [rows, kindFilter, typeFilter, dateRange, canUseIncome, canUseExpenses]);

    const totalIncome = filteredRows
        .filter((row) => row.kind === "income")
        .reduce((sum, row) => sum + row.amount, 0);
    const totalExpense = filteredRows
        .filter((row) => row.kind === "expense")
        .reduce((sum, row) => sum + row.amount, 0);
    const netProfit = totalIncome - totalExpense;
    const summarizeByType = (kind: EntryKind) => Array.from(
        filteredRows
            .filter((row) => row.kind === kind)
            .reduce((totals, row) => {
                totals.set(row.typeName, (totals.get(row.typeName) || 0) + row.amount);
                return totals;
            }, new Map<string, number>()),
    ).sort((a, b) => b[1] - a[1]);
    const incomeBreakdown = summarizeByType("income");
    const expenseBreakdown = summarizeByType("expense");

    const summaryRows = useMemo<AccountingSummaryRow[]>(() => [
        // Subscription rows are intentionally grouped by package; member-level sales
        // remain out of the accounting table.
        ...incomeBreakdown.map(([typeName, amount]) => ({
            key: `income:${typeName}`,
            category: "income" as const,
            typeName,
            details: "Income by package",
            amount,
        })),
        ...filteredRows
            .filter((row) => row.kind === "expense")
            .map((row) => ({
                key: `expense:${row.id}`,
                category: row.source === "salary" ? "salary" as const : "expense" as const,
                typeName: row.source === "salary" ? "Paid Salary" : row.typeName || "Operating Expense",
                details: [dateText(row.date), row.note].filter((value) => value && value !== "-").join(" · ") || "-",
                amount: row.amount,
            })),
    ], [incomeBreakdown, filteredRows]);

    const exportAnalytics = () => {
        const escapeCsv = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
        const sourceLabel = (source: string) => ({
            subscription: "Subscription",
            pay_per_use_session: "Pay Per Use Session",
            salary: "Salary Payment",
            manual: "Manual",
        }[source] || source || "Manual");
        const csvRows: Array<Array<string | number>> = [
            ["Accounting analytics"],
            ["Period", `${dateRange.startDate} to ${dateRange.endDate}`],
            ["Total Income", totalIncome.toFixed(2)],
            ["Total Expenses", totalExpense.toFixed(2)],
            ["Net Profit", netProfit.toFixed(2)],
            [],
            ["Income Breakdown"],
            ["Type", "Amount"],
            ...incomeBreakdown.map(([type, amount]) => [type, amount.toFixed(2)]),
            [],
            ["Expense Breakdown"],
            ["Type", "Amount"],
            ...expenseBreakdown.map(([type, amount]) => [type, amount.toFixed(2)]),
            [],
            ["Detailed Entries"],
            ["Date", "Category", "Type", "Source", "Amount", "Note"],
            ...filteredRows.map((row) => [
                row.date,
                row.kind === "income" ? "Income" : "Expense",
                row.typeName,
                sourceLabel(row.source),
                row.amount.toFixed(2),
                row.note,
            ]),
        ];
        const csv = csvRows.map((row) => row.map(escapeCsv).join(",")).join("\r\n");
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `accounting-${dateRange.startDate}-${dateRange.endDate}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="p-6 w-full">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold">Accounting</h1>
                    <p className="text-sm text-gray-500">Income and expenses in one page with filters</p>
                </div>
                {(canUseIncome || canUseExpenses) && (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={exportAnalytics}
                            disabled={!filteredRows.length}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
                        >
                            Export Analytics CSV
                        </button>
                        {canEditAccounting && canUseExpenses && (
                            <button
                                type="button"
                                onClick={() => setShowExpenseModal(true)}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
                            >
                                Add Expense
                            </button>
                        )}
                        {canEditAccounting && canUseIncome && (
                            <button
                                type="button"
                                onClick={() => setShowIncomeModal(true)}
                                className="rounded-lg bg-[#ac120c] px-4 py-2 text-sm text-white hover:bg-[#8f100b]"
                            >
                                Add Income
                            </button>
                        )}
                    </div>
                )}
            </div>

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {canUseIncome && (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Income</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">${totalIncome.toFixed(2)}</p>
                    </div>
                )}
                {canUseExpenses && (
                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <p className="text-sm text-gray-500">Expenses</p>
                        <p className="text-2xl font-bold text-red-600 mt-1">${totalExpense.toFixed(2)}</p>
                    </div>
                )}
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <p className="text-sm text-gray-500">Net Profit</p>
                    <p className={`text-2xl font-bold mt-1 ${netProfit >= 0 ? "text-green-600" : "text-red-600"}`}>
                        ${netProfit.toFixed(2)}
                    </p>
                </div>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h2 className="mb-3 font-semibold">Income by Package</h2>
                    {incomeBreakdown.length ? incomeBreakdown.map(([type, amount]) => (
                        <div key={type} className="flex justify-between border-t border-gray-100 py-2 text-sm">
                            <span>{type}</span><span className="font-semibold text-green-700">${amount.toFixed(2)}</span>
                        </div>
                    )) : <p className="text-sm text-gray-500">No income in this period.</p>}
                </div>
                <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <h2 className="mb-3 font-semibold">Expense Details</h2>
                    {expenseBreakdown.length ? expenseBreakdown.map(([type, amount]) => (
                        <div key={type} className="flex justify-between border-t border-gray-100 py-2 text-sm">
                            <span>{type}</span><span className="font-semibold text-red-700">${amount.toFixed(2)}</span>
                        </div>
                    )) : <p className="text-sm text-gray-500">No expenses in this period.</p>}
                </div>
            </div>

            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div className="flex flex-col">
                    <label className="text-sm mb-1">Category</label>
                    <select
                        value={kindFilter}
                        onChange={(e) => {
                            setKindFilter(e.target.value as "all" | EntryKind);
                            setTypeFilter("all");
                        }}
                        className="border p-2 rounded-md"
                    >
                        <option value="all">All</option>
                        {canUseIncome && <option value="income">Income</option>}
                        {canUseExpenses && <option value="expense">Expenses</option>}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label className="text-sm mb-1">Type</label>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="border p-2 rounded-md"
                    >
                        <option value="all">All</option>
                        {availableTypes.map((typeName) => (
                            <option key={typeName} value={typeName}>
                                {typeName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col">
                    <label className="text-sm mb-1">Start</label>
                    <input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                        className="border p-2 rounded-md"
                    />
                </div>

                <div className="flex flex-col">
                    <label className="text-sm mb-1">End</label>
                    <input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                        className="border p-2 rounded-md"
                    />
                </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="w-full min-w-[800px]">
                    <thead className="bg-black text-white">
                        <tr>
                            <th className="text-left p-3">Category</th>
                            <th className="text-left p-3">Package / Type</th>
                            <th className="text-left p-3">Details</th>
                            <th className="text-left p-3">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <TableLoadingRow colSpan={4} label="Loading accounting summary..." />
                        ) : summaryRows.length ? (
                            summaryRows.map((row) => (
                                <tr key={row.key} className="border-t border-gray-100">
                                    <td className="p-3">
                                        <span className={`px-2 py-1 rounded text-xs ${
                                            row.category === "income"
                                                ? "bg-green-100 text-green-700"
                                                : row.category === "salary"
                                                    ? "bg-amber-100 text-amber-800"
                                                    : "bg-red-100 text-red-700"
                                        }`}>
                                            {row.category === "income" ? "Income" : row.category === "salary" ? "Paid Salary" : "Expense"}
                                        </span>
                                    </td>
                                    <td className="p-3">{row.typeName}</td>
                                    <td className="p-3 text-gray-600">{row.details}</td>
                                    <td className="p-3 font-semibold">${row.amount.toFixed(2)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className="p-4 text-center text-gray-500" colSpan={4}>No data found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {canUseIncome && <AddDialog title="Add Income" isOpen={showIncomeModal} onClose={closeIncomeModal}>
                <form onSubmit={handleAddIncome} className="space-y-3 text-black">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Income Type</label>
                        <select
                            value={incomeForm.typeId}
                            onChange={(e) => setIncomeForm((prev) => ({ ...prev, typeId: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        >
                            <option value="">Select type</option>
                            {incomeTypes.map((typeItem) => (
                                <option key={typeItem.id} value={typeItem.id}>
                                    {typeItem.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Amount</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={incomeForm.amount}
                            onChange={(e) => setIncomeForm((prev) => ({ ...prev, amount: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Date</label>
                        <input
                            type="date"
                            value={incomeForm.date}
                            onChange={(e) => setIncomeForm((prev) => ({ ...prev, date: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Note</label>
                        <input
                            type="text"
                            value={incomeForm.note}
                            onChange={(e) => setIncomeForm((prev) => ({ ...prev, note: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full rounded-lg bg-[#ac120c] px-3 py-2 text-white hover:bg-[#8f100b] disabled:opacity-50"
                    >
                        {saving ? "Saving..." : "Save Income"}
                    </button>
                </form>
            </AddDialog>}

            {canUseExpenses && <AddDialog title="Add Expense" isOpen={showExpenseModal} onClose={closeExpenseModal}>
                <form onSubmit={handleAddExpense} className="space-y-3 text-black">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Expense Type</label>
                        <select
                            value={expenseForm.typeId}
                            onChange={(e) => setExpenseForm((prev) => ({ ...prev, typeId: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        >
                            <option value="">Select type</option>
                            {expenseTypes.map((typeItem) => (
                                <option key={typeItem.id} value={typeItem.id}>
                                    {typeItem.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Amount</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={expenseForm.amount}
                            onChange={(e) => setExpenseForm((prev) => ({ ...prev, amount: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Date</label>
                        <input
                            type="date"
                            value={expenseForm.date}
                            onChange={(e) => setExpenseForm((prev) => ({ ...prev, date: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-semibold">Note</label>
                        <input
                            type="text"
                            value={expenseForm.note}
                            onChange={(e) => setExpenseForm((prev) => ({ ...prev, note: e.target.value }))}
                            className="border px-3 py-2 rounded"
                            disabled={saving}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full rounded-lg bg-[#ac120c] px-3 py-2 text-white hover:bg-[#8f100b] disabled:opacity-50"
                    >
                        {saving ? "Saving..." : "Save Expense"}
                    </button>
                </form>
            </AddDialog>}
        </div>
    );
};

export default AccountingPage;
